// ============================================================================
// CrewAhead Maritime Platform - Shore API Functions
// ============================================================================
// Shore staff profiles, work experience, documents, job preferences,
// faceted search (employer-only), and dashboard.

import { apiClient, handleApiError } from './client';
import { API_ENDPOINTS } from '../utils/constants';

// ─── Types (canonical definitions in @/lib/types/models.ts) ─────────────────

import type {
  ShoreProfile,
  WorkExperience,
  ShoreDocument,
  JobPreference,
} from '@/lib/types';

export type { ShoreProfile, WorkExperience, ShoreDocument, JobPreference };

/**
 * ShoreProfileInput — write payload for ShoreProfileUpdateSerializer.
 *
 * FKs use *_id suffix (Django convention). No shore_category_id
 * because ShoreProfile model doesn't have that field.
 */
export interface ShoreProfileInput {
  currentPositionId?: number | null;   // maps to current_position_id
  currentPositionOther?: string;
  yearsOfExperience?: number | null;
  yearsAtSea?: number | null;
  headline?: string;
  summary?: string;
  highestEducation?: string;
  fieldOfStudy?: string;
  university?: string;
  hasSeaExperience?: boolean;
  highestSeaRankId?: number | null;    // maps to highest_sea_rank_id
  cocNumber?: string;
  availabilityStatus?: string;
  availableFrom?: string | null;
  noticePeriodDays?: number | null;
  skills?: string[];
  languages?: string[];
}

export interface WorkExperienceInput {
  companyName: string;
  jobTitle: string;       // REQUIRED — backend model has no blank=True
  positionId?: string;
  dateFrom: string;       // REQUIRED — backend model has no null=True
  dateTo?: string | null;
  isCurrent?: boolean;
  responsibilities?: string;
  location?: string;
}

export interface ShoreDocumentInput {
  title: string;
  documentType: string;
  description?: string;
  documentFile?: File;
  isPrimary?: boolean;
  expiryDate?: string | null;
}

export interface JobPreferenceInput {
  expectedSalaryMin?: number | null;
  expectedSalaryMax?: number | null;
  currency?: string;
  willingToRelocate?: boolean;
  preferredLocations?: string[];
  targetPositionIds?: string[];
  preferredRankIds?: string[];
  employmentType?: string | null;
}

export interface ShoreCertification {
  id: number;
  certificationTypeId: number | null;
  certificationName: string | null;
  certificationCode: string | null;
  certNameCustom: string;
  certificateNumber: string;
  issuingAuthority: string;
  issuingCountry: string;
  issueDate: string | null;
  expiryDate: string | null;
  isLifetime: boolean;
  documentFile: string | null;
  isVerified: boolean;
  expiryStatus: string | null;
  daysUntilExpiry: number | null;
  isExpired: boolean;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShoreCertificationInput {
  certificationTypeId?: number | null;
  certNameCustom?: string;
  certificateNumber?: string;
  issuingAuthority?: string;
  issuingCountry?: string;
  issueDate?: string | null;
  expiryDate?: string | null;
  isLifetime?: boolean;
  notes?: string;
}

export interface ShoreDashboardData {
  detail: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  stats: {
    profileCompletionPercent: number;
    experienceCount: number;
    documentCount: number;
    expiringDocuments: number;
  };
  profile: {
    currentPositionName: string | null;
    shoreCategoryName: string | null;
    yearsOfExperience: number | null;
    availabilityStatus: string | null;
    headline: string | null;
  };
}

export interface ShoreSearchFacets {
  availabilityStatus: Record<string, number>;
  hasSeaExperience: Record<string, number>;
}

// ─── My Profile ──────────────────────────────────────────────────────────────

/**
 * GET /shore/profile/
 *
 * Get the current user's shore profile.
 * Creates the profile if it doesn't exist.
 *
 * @returns Shore profile detail
 */
export async function getMyShoreProfile(): Promise<ShoreProfile> {
  try {
    const response = await apiClient.get<ShoreProfile>(
      API_ENDPOINTS.SHORE.MY_PROFILE
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PATCH /shore/profile/
 *
 * Update the current user's shore profile.
 *
 * @param data - Partial profile update data
 * @returns Updated shore profile
 */
export async function updateMyShoreProfile(
  data: Partial<ShoreProfileInput>
): Promise<ShoreProfile> {
  try {
    const response = await apiClient.patch<ShoreProfile>(
      API_ENDPOINTS.SHORE.MY_PROFILE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /shore/profiles/
 *
 * List shore profiles. Employers/admins see all;
 * regular users see only their own.
 *
 * @param params - Optional search and pagination
 * @returns Paginated list of shore profiles
 */
export async function getShoreProfiles(params?: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: ShoreProfile[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.SHORE.PROFILES}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: ShoreProfile[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /shore/profiles/{id}/
 *
 * Get a specific shore profile.
 *
 * @param id - Profile ID
 * @returns Shore profile detail
 */
export async function getShoreProfile(id: string): Promise<ShoreProfile> {
  try {
    const response = await apiClient.get<ShoreProfile>(
      API_ENDPOINTS.SHORE.PROFILE_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Work Experience ─────────────────────────────────────────────────────────

/**
 * GET /shore/experience/
 *
 * List work experience entries for the current user.
 *
 * @param params - Optional pagination
 * @returns Paginated list of work experience
 */
export async function getShoreWorkExperience(params?: {
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: WorkExperience[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.SHORE.EXPERIENCE}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: WorkExperience[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /shore/experience/
 *
 * Create a new work experience entry.
 *
 * @param data - Work experience data
 * @returns Created work experience
 */
export async function createWorkExperience(
  data: WorkExperienceInput
): Promise<WorkExperience> {
  try {
    const response = await apiClient.post<WorkExperience>(
      API_ENDPOINTS.SHORE.EXPERIENCE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /shore/experience/{id}/
 *
 * Get a single work experience entry.
 *
 * @param id - Work experience ID
 * @returns Work experience detail
 */
export async function getWorkExperience(id: string): Promise<WorkExperience> {
  try {
    const response = await apiClient.get<WorkExperience>(
      API_ENDPOINTS.SHORE.EXPERIENCE_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PUT /shore/experience/{id}/
 *
 * Update a work experience entry.
 *
 * @param id - Work experience ID
 * @param data - Updated work experience data
 * @returns Updated work experience
 */
export async function updateWorkExperience(
  id: string,
  data: Partial<WorkExperienceInput>
): Promise<WorkExperience> {
  try {
    const response = await apiClient.patch<WorkExperience>(
      API_ENDPOINTS.SHORE.EXPERIENCE_DETAIL(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * DELETE /shore/experience/{id}/
 *
 * Delete a work experience entry.
 *
 * @param id - Work experience ID
 */
export async function deleteWorkExperience(id: string): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.SHORE.EXPERIENCE_DETAIL(id));
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Documents ───────────────────────────────────────────────────────────────

/**
 * GET /shore/documents/
 *
 * List shore documents for the current user.
 *
 * @param params - Optional filters, search, and pagination
 * @returns Paginated list of documents
 */
export async function getShoreDocuments(params?: {
  documentType?: string;
  isPrimary?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: ShoreDocument[];
}> {
  try {
    const query = new URLSearchParams();
    if (params?.documentType) query.set('document_type', params.documentType);
    if (params?.isPrimary !== undefined) query.set('is_primary', String(params.isPrimary));
    if (params?.search) query.set('search', params.search);
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.SHORE.DOCUMENTS}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: ShoreDocument[];
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /shore/documents/
 *
 * Create a new shore document.
 *
 * @param data - Document data
 * @returns Created document
 */
export async function createShoreDocument(
  data: ShoreDocumentInput
): Promise<ShoreDocument> {
  try {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('document_type', data.documentType);
    if (data.description) formData.append('description', data.description);
    if (data.documentFile) formData.append('document_file', data.documentFile);
    if (data.isPrimary !== undefined) formData.append('is_primary', String(data.isPrimary));
    if (data.expiryDate) formData.append('expiry_date', data.expiryDate);

    const response = await apiClient.post<ShoreDocument>(
      API_ENDPOINTS.SHORE.DOCUMENTS,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /shore/documents/{id}/
 *
 * Get a single shore document.
 *
 * @param id - Document ID
 * @returns Document detail
 */
export async function getShoreDocument(id: string): Promise<ShoreDocument> {
  try {
    const response = await apiClient.get<ShoreDocument>(
      API_ENDPOINTS.SHORE.DOCUMENT_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * DELETE /shore/documents/{id}/
 *
 * Delete a shore document.
 *
 * @param id - Document ID
 */
export async function deleteShoreDocument(id: string): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.SHORE.DOCUMENT_DETAIL(id));
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Certifications ────────────────────────────────────────────────────────

/**
 * GET /shore/profiles/my-profile/certifications/
 *
 * List certifications for the current shore user.
 *
 * @returns List of shore certifications
 */
export async function getMyShoreProfileCertifications(): Promise<ShoreCertification[]> {
  try {
    const response = await apiClient.get<ShoreCertification[]>(
      API_ENDPOINTS.SHORE.MY_PROFILE_CERTIFICATIONS
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /shore/profiles/my-profile/certifications/
 *
 * Create a certification for the current shore user.
 *
 * @param data - Certification data
 * @returns Created shore certification
 */
export async function createMyShoreProfileCertification(
  data: Partial<ShoreCertificationInput>
): Promise<ShoreCertification> {
  try {
    const response = await apiClient.post<ShoreCertification>(
      API_ENDPOINTS.SHORE.MY_PROFILE_CERTIFICATIONS,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PATCH /shore/profiles/my-profile/certifications/{id}/
 *
 * Update a certification for the current shore user.
 *
 * @param id - Certification ID
 * @param data - Updated certification data
 * @returns Updated shore certification
 */
export async function updateMyShoreProfileCertification(
  id: number,
  data: Partial<ShoreCertificationInput>
): Promise<ShoreCertification> {
  try {
    const response = await apiClient.patch<ShoreCertification>(
      API_ENDPOINTS.SHORE.MY_PROFILE_CERTIFICATION_DETAIL(String(id)),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * DELETE /shore/profiles/my-profile/certifications/{id}/
 *
 * Delete a certification for the current shore user.
 *
 * @param id - Certification ID
 */
export async function deleteMyShoreProfileCertification(id: number): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.SHORE.MY_PROFILE_CERTIFICATION_DETAIL(String(id)));
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Job Preferences ─────────────────────────────────────────────────────────

/**
 * GET /shore/preferences/
 *
 * List job preferences for the current user.
 *
 * @returns Job preferences
 */
export async function getJobPreferences(): Promise<JobPreference[]> {
  try {
    const response = await apiClient.get<JobPreference[]>(
      API_ENDPOINTS.SHORE.PREFERENCES
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * GET /shore/preferences/{id}/
 *
 * Get a single job preference.
 *
 * @param id - Job preference ID
 * @returns Job preference detail
 */
export async function getJobPreference(id: string): Promise<JobPreference> {
  try {
    const response = await apiClient.get<JobPreference>(
      API_ENDPOINTS.SHORE.PREFERENCE_DETAIL(id)
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * POST /shore/preferences/
 *
 * Create new job preferences.
 *
 * @param data - Job preference data
 * @returns Created job preference
 */
export async function createJobPreference(
  data: JobPreferenceInput
): Promise<JobPreference> {
  try {
    const response = await apiClient.post<JobPreference>(
      API_ENDPOINTS.SHORE.PREFERENCES,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * PUT /shore/preferences/{id}/
 *
 * Update job preferences.
 *
 * @param id - Job preference ID
 * @param data - Updated preference data
 * @returns Updated job preference
 */
export async function updateJobPreference(
  id: string,
  data: Partial<JobPreferenceInput>
): Promise<JobPreference> {
  try {
    const response = await apiClient.put<JobPreference>(
      API_ENDPOINTS.SHORE.PREFERENCE_DETAIL(id),
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Search ──────────────────────────────────────────────────────────────────

/**
 * GET /shore/search/
 *
 * Faceted search for shore staff — employer/admin only.
 *
 * @param params - Optional search filters and pagination
 * @returns Paginated list of shore profiles
 */
export async function searchShoreProfiles(params?: {
  search?: string;
  availability?: string;
  minExperience?: number;
  maxExperience?: number;
  hasSeaExperience?: boolean;
  skills?: string;
  position?: string;
  maxNoticePeriod?: number;
  availableFrom?: string;
  willingToRelocate?: boolean;
  minSalary?: number;
  maxSalary?: number;
  minCompletion?: number;
  facets?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<{
  count: number;
  results: ShoreProfile[];
  facets?: ShoreSearchFacets;
}> {
  try {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.availability) query.set('availability', params.availability);
    if (params?.minExperience !== undefined) query.set('min_experience', String(params.minExperience));
    if (params?.maxExperience !== undefined) query.set('max_experience', String(params.maxExperience));
    if (params?.hasSeaExperience !== undefined) query.set('has_sea_experience', String(params.hasSeaExperience));
    if (params?.skills) query.set('skills', params.skills);
    if (params?.position) query.set('position', params.position);
    if (params?.maxNoticePeriod !== undefined) query.set('max_notice_period', String(params.maxNoticePeriod));
    if (params?.availableFrom) query.set('available_from', params.availableFrom);
    if (params?.willingToRelocate !== undefined) query.set('willing_to_relocate', String(params.willingToRelocate));
    if (params?.minSalary !== undefined) query.set('min_salary', String(params.minSalary));
    if (params?.maxSalary !== undefined) query.set('max_salary', String(params.maxSalary));
    if (params?.minCompletion !== undefined) query.set('min_completion', String(params.minCompletion));
    if (params?.facets) query.set('facets', 'true');
    if (params?.page !== undefined) query.set('page', String(params.page));
    if (params?.pageSize !== undefined) query.set('page_size', String(params.pageSize));

    const qs = query.toString();
    const url = `${API_ENDPOINTS.SHORE.SEARCH}${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<{
      count: number;
      results: ShoreProfile[];
      facets?: ShoreSearchFacets;
    }>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

/**
 * GET /shore/profile/dashboard/
 *
 * Return shore user dashboard data.
 *
 * @returns Shore dashboard data
 */
export async function getShoreDashboard(): Promise<ShoreDashboardData> {
  try {
    const response = await apiClient.get<ShoreDashboardData>(
      API_ENDPOINTS.SHORE.DASHBOARD
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Taxonomy-Aligned Write Payloads ─────────────────────────────────────────

export interface WorkExperienceWriteInput {
  id?: number;
  jobTitle: string;
  positionId?: number | null;
  positionOther?: string;
  companyName: string;
  companyType?: string;
  location?: string;
  isRemote?: boolean;
  dateFrom: string;
  dateTo?: string | null;
  isCurrent?: boolean;
  department?: string;
  responsibilities?: string;
  achievements?: string;
}

export interface JobPreferenceWriteInput {
  targetPositions?: number[];
  targetPositionsOther?: string[];
  employmentTypes?: string[];
  preferredLocations?: string[];
  willingToRelocate?: boolean;
  willingToTravel?: boolean;
  travelPercentage?: number | null;
  expectedSalaryMin?: number | null;
  expectedSalaryMax?: number | null;
  salaryCurrency?: string;
  preferredSectors?: string[];
  companyTypes?: string[];
  onboardAllowed?: boolean;
  preferredRanks?: number[];
}

export interface ShoreProfileWriteInput {
  currentPositionId?: number | null;
  highestSeaRankId?: number | null;
  currentPositionOther?: string;
  yearsOfExperience?: number | null;
  yearsAtSea?: number | null;
  headline?: string;
  summary?: string;
  highestEducation?: string;
  fieldOfStudy?: string;
  university?: string;
  graduationYear?: number | null;
  gradeOrResult?: string;
  hasSeaExperience?: boolean;
  cocNumber?: string;
  /** FK ID to seamaster.AvailabilityStatus — sent as availability_status_id */
  availabilityStatusId?: number | null;
  availableFrom?: string | null;
  noticePeriodDays?: number | null;
  skills?: number[];
  languages?: string[];
  /** M2M IDs to seamaster.Course */
  courseIds?: number[];
  /** M2M IDs to seamaster.ShoreCategory */
  preferredShoreCategoryIds?: number[];
  isVisibleToEmployers?: boolean;
  is_visible_to_employers?: boolean;
  workExperience?: WorkExperienceWriteInput[];
  jobPreference?: JobPreferenceWriteInput;
}

/**
 * PATCH /shore/profile
 *
 * Update the current user's shore profile including nested work_experience
 * and job_preference. The axios client converts camelCase to snake_case.
 *
 * @param data - Partial profile update data
 * @returns Updated shore profile
 */
export async function patchMyShoreProfile(
  data: Partial<ShoreProfileWriteInput>
): Promise<ShoreProfile> {
  try {
    const response = await apiClient.patch<ShoreProfile>(
      API_ENDPOINTS.SHORE.MY_PROFILE,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
